
// Vue
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action } from "vuex-class";

// Third Party
import { validationMixin } from "vuelidate";
import { required, requiredIf, minLength, maxLength, numeric, minValue, not } from "vuelidate/lib/validators";
import { isEqual, debounce, get } from "lodash";

// Components
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import vSelect from "vselect3";

import { ExternalMenuLink } from "@/store/external-menu-link/types";
import DeleteExternalMenuLinkModal from "@/components/external-menu-link/DeleteExternalMenuLinkModal.vue";
import { UserPermissions, FeaturesList } from "@/store/types";

// Namespaces
const ExternalMenuLinkStore = namespace("externalMenuLink");

@Component({
	components: {
		"v-select": vSelect,
		VuePerfectScrollbar: VuePerfectScrollbar,
		"delete-external-menu-link-modal": DeleteExternalMenuLinkModal
	}
})
export default class ExternalMenuLinkModal extends Vue {
	@Prop({ default: null, type: Object }) selectedExternalMenuLink: ExternalMenuLink;
	@Prop({ default: false, type: Boolean }) visible: boolean;
	@Prop({ default: true, type: Boolean }) readonly: boolean;

	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter getPermissions: UserPermissions;

	@ExternalMenuLinkStore.Action createExternalMenuLink: (externalMenuLink: FormData) => Promise<void>;
	@ExternalMenuLinkStore.Action updateExternalMenuLink: (externalMenuLink: FormData) => Promise<void>;

	private showDeleteModal: boolean = false;
	private isNew: boolean = true;
	private file: File = null;
	private useFile: boolean = false;

	private readonly emlTemplate: ExternalMenuLink = {
		externalMenuLinkId: 0,
		title: "",
		link: "",
		text: "",
		isNewTab: true,
		iconName: "",
		iconFile: null
	};

	private currentExternalMenuLink: ExternalMenuLink = { ...this.emlTemplate };

	@Emit("toggle-modal")
	@Emit("external-menu-link")
	private closeModal(): void {
		this.selectedExternalMenuLink = { ...this.emlTemplate };
	}

	@Watch("visible")
	private modalToggle(toggle: boolean) {
		this.isNew = !this.selectedExternalMenuLink;
		if (toggle) {
			this.currentExternalMenuLink = this.isNew ? { ...this.emlTemplate } : { ...this.selectedExternalMenuLink };
		} else {
			this.currentExternalMenuLink = { ...this.emlTemplate };
		}
	}

	private async saveExternalMenuLink(): Promise<void> {
		if (this.file) {
			// @ts-ignore
			this.currentExternalMenuLink.iconFile = this.file;
		}

		let formData = new FormData();

		for (let [name, value] of Object.entries(this.currentExternalMenuLink)) {
			formData.append(name, value);
		}
		if (this.isNew) {
			await this.createExternalMenuLink(formData);
		} else {
			await this.updateExternalMenuLink(formData);
		}
		this.currentExternalMenuLink = { ...this.emlTemplate };
		this.file = null;
		this.useFile = false;
		this.closeModal();
	}

	public get isExternalMenuLinkEnabled() {
		return (
			get(this.featuresList, ["NavMenu", "ExternalMenuLinks"]) &&
			(this.getPermissions.isAdmin ||
				this.getPermissions.canViewExternalMenuLinks ||
				this.getPermissions.canEditExternalMenuLinks)
		);
	}

	public get titleAlreadyExists(): boolean {
		return (
			!this.$v.currentExternalMenuLink.title.doesntExist &&
			this.currentExternalMenuLink.title.length > 0 &&
			this.isNew
		);
	}

	@Watch("useFile")
	public onIconToggle(): void {
		if (this.useFile == true) {
			this.currentExternalMenuLink.iconName = null;
		} else {
			this.currentExternalMenuLink.iconFile = null;
		}
	}
}
